import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement} [searchSuggestions] - The container Athos renders its autocomplete panel into.
 * @property {HTMLFormElement} [form] - The search form; on mobile it is also the drawer's scroll container.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * @type {MutationObserver | null}
   */
  #suggestionsObserver = null;

  /**
   * True during the close sequence so the synchronous re-focus triggered by
   * #resetSearch (via the #currentIndex setter) cannot re-open the overlay.
   * @type {boolean}
   */
  #closing = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, {
        signal,
      });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    } else {
      this.#setupInlineMode(signal);
    }

    this.#observeSuggestions();

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Keep the combobox's `aria-expanded` in step with the Athos autocomplete.
   * Athos renders the panel into `searchSuggestions` only while it is open and
   * removes it on close, so the container's child count is the open state.
   */
  #observeSuggestions() {
    const { searchSuggestions, searchInput } = this.refs;

    if (!searchSuggestions) return;

    const syncExpanded = () => {
      searchInput.setAttribute('aria-expanded', String(searchSuggestions.childElementCount > 0));
    };

    this.#suggestionsObserver = new MutationObserver(syncExpanded);
    this.#suggestionsObserver.observe(searchSuggestions, { childList: true });
    syncExpanded();
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * Inline (header, no-dialog) mode. The header pill is the live input; results
   * drop into a full-width panel (desktop) or a left drawer (mobile) with the
   * page dimmed via the shared header overlay, mirroring the mega-menu.
   * @param {AbortSignal} signal
   */
  #setupInlineMode(signal) {
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.hasAttribute('open')) {
          this.close();
        }
      },
      { signal },
    );

    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!this.hasAttribute('open')) return;
        if (!this.contains(/** @type {Node} */ (event.target))) {
          this.close();
        }
      },
      { signal },
    );

    // Hovering a top-level menu item hands the header overlay to the mega menu.
    // `pointerover` because `pointerenter` doesn't bubble to document.
    document.addEventListener(
      'pointerover',
      (event) => {
        if (!this.hasAttribute('open')) return;
        const target = event.target;
        if (target instanceof Element && target.closest('.menu-list__link')) {
          this.close();
        }
      },
      { signal },
    );

    this.addEventListener('click', this.#handleModalClick, { signal });

    const { form } = this.refs;

    form?.addEventListener(
      'scroll',
      () => form.classList.toggle('header-search__form--scrolled', form.scrollTop > 0),
      { signal, passive: true },
    );
  }

  #isMobileViewport() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  /**
   * Anchor the mobile drawer to the top of the viewport. The drawer is a fixed
   * child of #header-component, which has `contain: layout` and therefore acts
   * as its containing block — so `top: 0` lands at the header's top (below the
   * announcement bar), not the viewport top. Offset it upward by the header's
   * current viewport position so the drawer covers the full screen.
   */
  #anchorDrawer() {
    const header = document.getElementById('header-component') ?? this.closest('.header');
    const top = header instanceof HTMLElement ? header.getBoundingClientRect().top : 0;
    this.style.setProperty('--search-drawer-top', `${-top}px`);
  }

  /**
   * Open the overlay. Loads the empty state (incl. recently viewed) on first open.
   */
  open() {
    if (this.#closing || this.hasAttribute('open')) return;
    this.setAttribute('open', '');
    if (this.#isMobileViewport()) {
      this.#anchorDrawer();
      document.documentElement.setAttribute('scroll-lock', '');
    }
    this.#loadEmptyState();
  }

  /**
   * Open handler for the desktop input focus.
   */
  handleFocus = () => {
    this.open();
  };

  /**
   * Open handler for the mobile trigger icon — opens the drawer and focuses the input.
   */
  openMobile = () => {
    this.open();
    requestAnimationFrame(() => this.refs.searchInput?.focus());
  };

  /**
   * Close the overlay and reset the search back to its empty state.
   * `#closing` blocks the re-focus that #resetSearch performs from re-opening us.
   */
  close = () => {
    if (!this.hasAttribute('open')) return;
    this.#closing = true;
    this.removeAttribute('open');
    document.documentElement.removeAttribute('scroll-lock');
    this.refs.searchInput.value = '';
    this.#hideResetButton();
    this.#resetSearch();
    this.refs.searchInput.blur();
    requestAnimationFrame(() => {
      this.#closing = false;
      if (document.activeElement === this.refs.searchInput) {
        this.refs.searchInput.blur();
      }
    });
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#suggestionsObserver?.disconnect();
    this.#suggestionsObserver = null;
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();

    // Clear the header's desktop input value on close
    const headerInput = document.querySelector('.search-action__desktop-input');
    if (headerInput instanceof HTMLInputElement) {
      headerInput.value = '';
    }
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }

    // Auto-focus the modal's search input after dialog opens
    requestAnimationFrame(() => {
      if (this.refs.searchInput) {
        this.refs.searchInput.focus();
      }
    });
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list',
      ),
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'nearest',
    });
    if (this.hasAttribute('open')) {
      this.refs.searchInput.focus();
    }
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      // Tab is intentionally NOT handled: let it fall through to native focus
      // so keyboard users can reach every link (incl. sidebar pills). Arrow
      // keys drive the roving highlight; Tab drives real DOM focus.

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        // If a link/button already has native focus (via Tab), let it activate
        // itself — don't hijack Enter into the search-submit fallback.
        const target = /** @type {HTMLElement} */ (event.target);
        if (target.closest('a, button') && target !== this.refs.searchInput) {
          return;
        }

        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Apply a popular-search term as if the user typed it: fill the input and run
   * the predictive search inline instead of navigating to the results page.
   * @param {Event} event
   */
  applyPopularSearch = (event) => {
    event.preventDefault();

    const target = /** @type {HTMLElement | null} */ (event.target);
    const trigger = target?.closest('[data-search-term]');
    const term = (trigger instanceof HTMLElement ? trigger.dataset.searchTerm ?? '' : '').trim();
    if (!term) return;

    const { searchInput } = this.refs;
    searchInput.value = term;
    searchInput.focus();
    this.#currentIndex = -1;
    this.#showResetButton();
    this.#getSearchResults(term);
  };

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[type]', 'query,product,collection');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      // Recently viewed REPLACES the default product row rather than joining it —
      // the panel shows one row of products, never both.
      const defaultProductsElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!defaultProductsElement) return;
      defaultProductsElement.replaceWith(recentlyViewedProductsHtml);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
